"use client";

import React from "react";
import { Activity, Mic, MessageSquareDashed, SmilePlus, Info } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
const paceData = [{
  time: "08:00",
  wpm: 12
}, {
  time: "10:00",
  wpm: 45
}, {
  time: "12:00",
  wpm: 32
}, {
  time: "14:00",
  wpm: 88
}, {
  time: "16:00",
  wpm: 54
}, {
  time: "18:00",
  wpm: 21
}];
const toneData = [{
  time: "Mon",
  energy: 40
}, {
  time: "Tue",
  energy: 65
}, {
  time: "Wed",
  energy: 55
}, {
  time: "Thu",
  energy: 90
}, {
  time: "Fri",
  energy: 75
}, {
  time: "Sat",
  energy: 60
}];
const fillerData = [{
  word: "Steel",
  count: 85
}, {
  word: "Grain",
  count: 62
}, {
  word: "Fuel",
  count: 45
}, {
  word: "Lumber",
  count: 30
}];
const sentimentData = [{
  time: "Q1",
  score: 0.4
}, {
  time: "Q2",
  score: 0.6
}, {
  time: "Q3",
  score: 0.5
}, {
  time: "Q4",
  score: 0.8
}, {
  time: "Q1",
  score: 0.9
}, {
  time: "Q2",
  score: 0.85
}];
const metrics = [{
  id: "pace",
  title: "Real-Time Tracking",
  description: "Monitor your active orders and logistics in real-time.",
  icon: Activity,
  colorClass: "text-accent",
  bgClass: "bg-accent/10",
  borderClass: "border-accent/20",
  detailTitle: "Logistics Velocity",
  detailText: "Current transit efficiency is up 12% compared to last month, with peak delivery volumes occurring at 14:00.",
  chartConfig: {
    wpm: {
      label: "Orders",
      color: "var(--color-accent)"
    }
  },
  renderChart: () => <LineChart data={paceData} margin={{
    top: 5,
    right: 5,
    left: -20,
    bottom: 0
  }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" opacity={0.5} />
        <XAxis dataKey="time" tickLine={false} axisLine={false} tick={{
      fontSize: 10,
      fill: 'var(--color-muted-foreground)'
    }} />
        <YAxis tickLine={false} axisLine={false} tick={{
      fontSize: 10,
      fill: 'var(--color-muted-foreground)'
    }} />
        <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
        <Line type="monotone" dataKey="wpm" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
      </LineChart>
}, {
  id: "tone",
  title: "Quality Reports",
  description: "Access detailed quality metrics directly from suppliers.",
  icon: Mic,
  colorClass: "text-secondary",
  bgClass: "bg-secondary/10",
  borderClass: "border-secondary/20",
  detailTitle: "Compliance & Quality",
  detailText: "Supplier quality scores reached a peak of 90% this Thursday, maintaining a high standard across all batches.",
  chartConfig: {
    energy: {
      label: "Quality Score",
      color: "var(--color-secondary)"
    }
  },
  renderChart: () => <AreaChart data={toneData} margin={{
    top: 5,
    right: 5,
    left: -20,
    bottom: 0
  }}>
        <defs>
          <linearGradient id="fillEnergy" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--color-secondary)" stopOpacity={0.3} />
            <stop offset="95%" stopColor="var(--color-secondary)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" opacity={0.5} />
        <XAxis dataKey="time" tickLine={false} axisLine={false} tick={{
      fontSize: 10,
      fill: 'var(--color-muted-foreground)'
    }} />
        <YAxis tickLine={false} axisLine={false} tick={{
      fontSize: 10,
      fill: 'var(--color-muted-foreground)'
    }} />
        <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
        <Area type="monotone" dataKey="energy" stroke="var(--color-secondary)" fillOpacity={1} fill="url(#fillEnergy)" />
      </AreaChart>
}, {
  id: "filler",
  title: "Market Analytics",
  description: "Analyze global market trends and dynamic pricing.",
  icon: MessageSquareDashed,
  colorClass: "text-accent",
  bgClass: "bg-accent/10",
  borderClass: "border-accent/20",
  detailTitle: "Commodity Price Index",
  detailText: "Steel and Grain remain the highest volume commodities this quarter, showing steady price stability.",
  chartConfig: {
    count: {
      label: "Price Index",
      color: "var(--color-accent)"
    }
  },
  renderChart: () => <BarChart data={fillerData} margin={{
    top: 5,
    right: 5,
    left: -20,
    bottom: 0
  }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" opacity={0.5} />
        <XAxis dataKey="word" tickLine={false} axisLine={false} tick={{
      fontSize: 10,
      fill: 'var(--color-muted-foreground)'
    }} />
        <YAxis tickLine={false} axisLine={false} tick={{
      fontSize: 10,
      fill: 'var(--color-muted-foreground)'
    }} />
        <ChartTooltip content={<ChartTooltipContent indicator="dashed" />} />
        <Bar dataKey="count" fill="var(--color-accent)" radius={[2, 2, 0, 0]} />
      </BarChart>
}, {
  id: "sentiment",
  title: "Supplier Ratings",
  description: "View and compare verified supplier performance ratings.",
  icon: SmilePlus,
  colorClass: "text-primary",
  bgClass: "bg-primary/10",
  borderClass: "border-primary/20",
  detailTitle: "Performance Benchmarking",
  detailText: "Supplier reliability scores have trended upward through Q1, indicating improved supply chain stability.",
  chartConfig: {
    score: {
      label: "Reliability",
      color: "var(--color-primary)"
    }
  },
  renderChart: () => <LineChart data={sentimentData} margin={{
    top: 5,
    right: 5,
    left: -20,
    bottom: 0
  }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" opacity={0.5} />
        <XAxis dataKey="time" tickLine={false} axisLine={false} tick={{
      fontSize: 10,
      fill: 'var(--color-muted-foreground)'
    }} />
        <YAxis tickLine={false} axisLine={false} tick={{
      fontSize: 10,
      fill: 'var(--color-muted-foreground)'
    }} />
        <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
        <Line type="stepAfter" dataKey="score" stroke="var(--color-primary)" strokeWidth={2} dot={{
      r: 3,
      fill: "var(--color-primary)"
    }} />
      </LineChart>
}];
export default function RealTimeAnalysis() {
  return <section data-section-id="245097" id="buyer-portal-features" className="bg-background text-foreground py-24 px-6 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-accent/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-default text-3xl md:text-5xl font-bold mb-6 tracking-tight">
            Advanced <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Buyer Intelligence</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-sans">
            Our portal provides a comprehensive suite of tools to manage your procurement lifecycle. Hover over each module to explore real-time data visualizations and strategic insights.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, i) => <div data-index={i} key={metric.id} className="h-full">
              <HoverCard openDelay={100} closeDelay={200}>
                <HoverCardTrigger asChild>
                  <div className={`group relative h-full p-6 rounded-lg bg-white backdrop-blur-sm border ${metric.borderClass} hover:shadow-medium transition-all duration-300 cursor-pointer overflow-hidden flex flex-col`}>
                    {}
                    <div className={`absolute inset-0 ${metric.bgClass} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    
                    <div className="relative z-10 flex-1">
                      <div className="flex items-start justify-between mb-6">
                        <div className={`p-3 rounded-md bg-background border ${metric.borderClass}`}>
                          <metric.icon className={`w-6 h-6 ${metric.colorClass}`} />
                        </div>
                        <Info className="w-5 h-5 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" />
                      </div>
                      
                      <h3 className="font-default text-xl font-semibold mb-2 text-primary">
                        {metric.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed font-sans">
                        {metric.description}
                      </p>
                    </div>

                    <div className="relative z-10 mt-6 pt-4 border-t border-border/50 flex items-center text-xs font-medium text-muted-foreground group-hover:text-primary transition-colors">
                      <span className="font-display uppercase tracking-wider">View Analytics</span>
                      <div className="ml-auto flex space-x-1">
                        <span className="w-1 h-1 rounded-full bg-accent animate-bounce" style={{
                      animationDelay: '0ms'
                    }} />
                        <span className="w-1 h-1 rounded-full bg-accent animate-bounce" style={{
                      animationDelay: '150ms'
                    }} />
                        <span className="w-1 h-1 rounded-full bg-accent animate-bounce" style={{
                      animationDelay: '300ms'
                    }} />
                      </div>
                    </div>
                  </div>
                </HoverCardTrigger>
                
                <HoverCardContent side="bottom" align="center" className="w-80 sm:w-96 bg-white/95 backdrop-blur-xl border-border shadow-strong p-5 rounded-lg">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-default text-base font-semibold text-primary flex items-center gap-2">
                        <metric.icon className={`w-4 h-4 ${metric.colorClass}`} />
                        {metric.detailTitle}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1 font-sans">
                        {metric.detailText}
                      </p>
                    </div>
                    
                    <div className="h-40 w-full mt-4 bg-primary-light/30 rounded-sm p-2 border border-border">
                      <ChartContainer config={metric.chartConfig} className="h-full w-full">
                        {metric.renderChart()}
                      </ChartContainer>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>)}
        </div>
      </div>
    </section>;
}